import { NavLink, Link, useLocation } from 'react-router-dom';
import { useEffect, useState, type ReactNode } from 'react';
import { useSession, useToast } from '../lib/store';
import { Button, Confetti, Modal } from './ui';
import { Icon, type IconName } from './icons';
import { ChatDock } from './chat/ChatDock';
import { hasSeenTour, OverhaulTour } from './OverhaulTour';
import { NotificationBell } from './NotificationBell';
import { ClaimPrompt } from './ClaimPrompt';
import { BadgeFanfareQueue } from './badges/BadgeFanfare';
import { formatNumber } from '../lib/format';
import { DISCORD_INVITE } from '../lib/links';

interface NavItem {
  to: string;
  label: string;
  icon: IconName;
  end?: boolean;
}

/**
 * Navigation lives in a left rail, grouped by what the player is trying to do.
 * Grouping matters more here than it would horizontally: eight flat links in a
 * column is a wall, but three short labelled runs are scannable.
 *
 * Each group carries an accent colour, applied by `game.css` to its heading,
 * its icons and its active marker. Eighteen links in one gold made the three
 * runs read as one long list — a glance at the colour now tells you which part
 * of the game you are in before you have read a single word. The colours are
 * the existing palette roles, not new ones: growing things are leaf, the
 * collection is sky, and anything involving other people is gold.
 */
/**
 * Nine top-level destinations, each of which may own a short run of pages.
 *
 * The rail used to be three groups of eighteen flat links, one of which was a
 * world map that existed mainly to connect the others. Removing it exposed the
 * real problem: everything was top-level, so nothing read as important. Now a
 * section is one link with its related pages nested under it, and the nesting
 * only appears when you are in that section — the rail shows nine items at
 * rest, not twenty-one.
 *
 * The ordering is the daily routine, top to bottom: check what is ready, look
 * after your Pokémon, then go and see other people.
 */
const NAV_SECTIONS: Array<{
  label: string;
  to: string;
  icon: IconName;
  accent: string;
  end?: boolean;
  children?: NavItem[];
}> = [
  { label: 'Home', to: '/', icon: 'home', accent: 'leaf', end: true },
  { label: 'Groves', to: '/grove', icon: 'grove', accent: 'leaf', end: true },
  {
    label: 'Eggs',
    to: '/hatchery',
    icon: 'egg',
    accent: 'leaf',
    children: [
      { to: '/hatchery', label: 'Hatchery', icon: 'egg' },
      { to: '/eggs', label: 'Egg Market', icon: 'basket' },
      { to: '/journal', label: 'Egg Journal', icon: 'book' },
    ],
  },
  {
    label: 'Breeding',
    to: '/nursery',
    icon: 'flower',
    accent: 'leaf',
    children: [
      { to: '/nursery', label: 'Nursery', icon: 'flower' },
      { to: '/breeder', label: 'Breeder', icon: 'book' },
    ],
  },
  {
    label: 'Collection',
    to: '/collection',
    icon: 'ball',
    accent: 'sky',
    children: [
      { to: '/collection', label: 'My Pokémon', icon: 'ball' },
      { to: '/companions', label: 'Companions', icon: 'heart' },
      { to: '/museum', label: 'Museum', icon: 'book' },
    ],
  },
  {
    label: 'Pokédex',
    to: '/dex',
    icon: 'list',
    accent: 'sky',
    children: [
      { to: '/dex', label: 'Pokédex', icon: 'list' },
      { to: '/badges', label: 'Badges', icon: 'shield' },
      { to: '/achievements', label: 'Achievements', icon: 'star' },
      { to: '/statistics', label: 'Statistics', icon: 'podium' },
    ],
  },
  {
    label: 'Bazaar',
    to: '/bazaar',
    icon: 'stall',
    accent: 'gold',
    children: [
      { to: '/bazaar', label: 'Browse', icon: 'stall' },
      { to: '/bazaar/mine', label: 'My Stall', icon: 'bag' },
      { to: '/trades', label: 'Trades', icon: 'swap' },
      { to: '/shop', label: 'Shop', icon: 'bag' },
    ],
  },
  { label: 'Guilds', to: '/guilds', icon: 'banner', accent: 'gold' },
  {
    label: 'Community',
    to: '/community',
    icon: 'people',
    accent: 'gold',
    children: [
      { to: '/community', label: 'Players', icon: 'people' },
      { to: '/events', label: 'Events', icon: 'sparkle' },
      { to: '/world-boss', label: 'World Boss', icon: 'bolt' },
      { to: '/friends', label: 'Friends', icon: 'heart' },
      { to: '/leaderboard', label: 'Leaderboards', icon: 'podium' },
      { to: '/hall-of-fame', label: 'Hall of Fame', icon: 'podium' },
      { to: '/encyclopedia', label: 'Encyclopedia', icon: 'book' },
      { to: '/team', label: 'The Team', icon: 'star' },
      { to: '/credits', label: 'Credits', icon: 'book' },
    ],
  },
];

/** Everything reachable from the rail, for the mobile "More" drawer. */
const ALL_NAV: NavItem[] = NAV_SECTIONS.flatMap((section) =>
  section.children ?? [{ to: section.to, label: section.label, icon: section.icon, end: section.end }],
);

/**
 * The four worth a thumb tap.
 *
 * Deliberately four and not five: the tab bar also carries "More", and five
 * plus More is six targets across a phone, which is under the size a thumb
 * hits reliably. These are the four things a player opens every single day.
 */
const MOBILE_NAV: NavItem[] = [
  { to: '/', label: 'Home', icon: 'home', end: true },
  { to: '/grove', label: 'Groves', icon: 'grove', end: true },
  { to: '/hatchery', label: 'Eggs', icon: 'egg' },
  { to: '/community', label: 'Community', icon: 'people' },
];

export function Shell({ children }: { children: ReactNode }) {
  const { me, logout, walletBumped, pendingAchievements, clearAchievement, pendingBadges, clearBadges } =
    useSession();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  // Checked once on mount rather than on every render: reading localStorage in
  // a render body would re-run on every keystroke anywhere in the app.
  const [tourOpen, setTourOpen] = useState(false);
  const [signOutConfirm, setSignOutConfirm] = useState(false);

  useEffect(() => setMenuOpen(false), [location.pathname]);

  useEffect(() => {
    if (me && !hasSeenTour(me.user.id)) setTourOpen(true);
  }, [me]);

  if (!me) return <>{children}</>;

  const eggsReady = me.eggsReady;
  // Navigation is gated on permissions, not on a role name — that is the
  // whole point of the system. `can` is display logic only.
  const can = (permission: string) => me.permissions?.includes(permission) ?? false;
  const isAdmin = can('admin.panel');
  const isStaff = can('staff.panel');

  const requestSignOut = () => {
    setMenuOpen(false);
    if (me.account.isGuest) setSignOutConfirm(true);
    else void logout();
  };

  const wallet = (
    <div className="wallet">
      <span className={`coin ${walletBumped ? 'coin--bumped' : ''}`} title="PokéCoins">
        <span className="coin__dot coin__dot--gold" aria-hidden />
        {formatNumber(me.wallet.pokeCoins)}
        <span className="coin__label">COINS</span>
      </span>
      <span className={`coin ${walletBumped ? 'coin--bumped' : ''}`} title="Friendship Tokens">
        <span className="coin__dot coin__dot--token" aria-hidden />
        {formatNumber(me.wallet.friendshipTokens)}
        <span className="coin__label">TOKENS</span>
      </span>
    </div>
  );

  return (
    <div className="shell">
      <aside className="rail">
        <Link to="/" className="brand">
          <img src="/logo-sm.png" alt="PokéGrove" className="brand__logo" />
        </Link>

        {wallet}
        <NotificationBell />

        <nav className="nav" aria-label="Main">
          {NAV_SECTIONS.map((section, index) => {
            // A section is "open" when you are anywhere inside it. Sub-pages
            // are hidden the rest of the time, which is what keeps the rail at
            // nine items rather than twenty-one — and means the sub-navigation
            // is a consequence of where you are, not another thing to manage.
            const inSection =
              section.children?.some((child) =>
                child.to === '/'
                  ? location.pathname === '/'
                  : location.pathname === child.to || location.pathname.startsWith(`${child.to}/`),
              ) ?? false;

            // The rail used to space three labelled groups apart. With nine
            // sections that spacing would apply between every single link, so
            // the separation now marks where the *accent changes* — the same
            // Play / Collect / Town runs as before, drawn as a hairline instead
            // of a heading and a gap.
            const startsRun = index > 0 && NAV_SECTIONS[index - 1]!.accent !== section.accent;

            return (
              <div
                className={`nav__section nav__section--${section.accent} ${
                  startsRun ? 'nav__section--rule' : ''
                }`}
                key={section.label}
              >
                <NavLink
                  to={section.to}
                  end={section.end}
                  className={({ isActive }) =>
                    `nav__link ${isActive || inSection ? 'nav__link--active' : ''}`
                  }
                >
                  <span className="nav__icon">
                    <Icon name={section.icon} />
                  </span>
                  {section.label}
                  {section.to === '/hatchery' && eggsReady > 0 && (
                    <span className="nav__dot" aria-label={`${eggsReady} ready`} />
                  )}
                </NavLink>

                {inSection && section.children && section.children.length > 1 && (
                  <div className="nav__sub">
                    {section.children.map((child) => (
                      <NavLink
                        key={child.to}
                        to={child.to}
                        end={child.end}
                        className={({ isActive }) =>
                          `nav__sublink ${isActive ? 'nav__sublink--active' : ''}`
                        }
                      >
                        {child.label}
                        {child.to === '/hatchery' && eggsReady > 0 && (
                          <span className="nav__dot" aria-label={`${eggsReady} ready`} />
                        )}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="rail__account">
          <div className="rail__user">
            {me.user.username}
            {isAdmin && <span className="rail__badge">ADMIN</span>}
          </div>
          {/* Always present for a guest, not only when the reminder strip is
              showing — a player who dismissed the strip still needs a way to
              claim, and hunting through Settings for it is not one. */}
          {me.account.isGuest && (
            <NavLink
              to="/claim"
              className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
            >
              <span className="nav__icon"><Icon name="star" /></span>
              Claim account
            </NavLink>
          )}
          <NavLink
            to={`/trainer/${me.user.username}`}
            className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
          >
            <span className="nav__icon"><Icon name="person" /></span>
            My Profile
          </NavLink>
          <NavLink
            to="/customise"
            className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
          >
            <span className="nav__icon"><Icon name="sparkle" /></span>
            Customise
          </NavLink>
          <NavLink
            to="/settings"
            className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
          >
            <span className="nav__icon"><Icon name="gear" /></span>
            Settings
          </NavLink>
          {isStaff && (
            <NavLink
              to="/staff"
              className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
            >
              <span className="nav__icon"><Icon name="shield" /></span>
              Moderation
            </NavLink>
          )}
          {isAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
            >
              <span className="nav__icon"><Icon name="gear" /></span>
              Admin
            </NavLink>
          )}
          {(can('events.view') || can('events.manage')) && (
            <NavLink
              to="/admin/events"
              className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
            >
              <span className="nav__icon"><Icon name="star" /></span>
              Events
            </NavLink>
          )}
          {can('roles.manage') && (
            <NavLink
              to="/admin/roles"
              className={({ isActive }) => `nav__link ${isActive ? 'nav__link--active' : ''}`}
            >
              <span className="nav__icon"><Icon name="people" /></span>
              Roles
            </NavLink>
          )}
          {/* The one link in the rail that leaves the site, so it says so with
              an arrow rather than only in the tooltip. `noopener` matters on
              every `target="_blank"`: without it the page that opens can reach
              back through `window.opener`. */}
          <a
            className="nav__link nav__link--out"
            href={DISCORD_INVITE}
            target="_blank"
            rel="noreferrer noopener"
          >
            <span className="nav__icon"><Icon name="discord" /></span>
            Discord
            <span className="nav__out" aria-label="opens in a new tab">↗</span>
          </a>
          <button type="button" className="nav__link" onClick={requestSignOut}>
            <span className="nav__icon"><Icon name="exit" /></span>
            Sign out
          </button>
        </div>
      </aside>

      <div className="shell__body">
        <header className="topbar">
          <Link to="/" className="brand">
            <img src="/logo-sm.png" alt="PokéGrove" className="brand__logo" />
          </Link>
          {wallet}
          <NotificationBell />
        </header>

        <ClaimPrompt />

        <main className="shell__main">{children}</main>
      </div>

      <nav className="tabbar" aria-label="Main">
        {MOBILE_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `tabbar__link ${isActive ? 'tabbar__link--active' : ''}`}
          >
            <span className="tabbar__icon"><Icon name={item.icon} size={20} /></span>
            {item.label}
            {item.to === '/hatchery' && eggsReady > 0 && <span className="nav__dot" aria-hidden />}
          </NavLink>
        ))}
        <button
          type="button"
          className="tabbar__link"
          onClick={() => setMenuOpen((open) => !open)}
          aria-label="More pages"
          aria-expanded={menuOpen}
        >
          <span className="tabbar__icon"><Icon name="more" size={20} /></span>
          More
        </button>
      </nav>

      {menuOpen && (
        <MobileMenu
          username={me.user.username}
          isAdmin={isAdmin}
          isGuest={me.account.isGuest}
          onClose={() => setMenuOpen(false)}
          onLogout={requestSignOut}
        />
      )}

      {/* Signing out is ordinarily unremarkable. For a guest it is the single
          destructive action in the game: the cookie *is* the account, so
          clearing it strands every Pokémon behind it. This is the one place a
          guest gets an interruption, and it is the one place that earns it. */}
      {signOutConfirm && (
        <Modal onClose={() => setSignOutConfirm(false)} labelledBy="signout-title">
          <div className="stack">
            <h2 id="signout-title" className="panel__title" style={{ margin: 0 }}>
              Sign out of an unclaimed account?
            </h2>
            <p className="tiny">
              This account is stored on this device only. Signing out clears it, and there is no way
              to get {me.profile.trainerName} — or any of their Pokémon — back.
            </p>
            <Link to="/claim" className="btn btn--go" onClick={() => setSignOutConfirm(false)}>
              Claim account first
            </Link>
            <div className="row row--between">
              <button type="button" className="linkbtn tiny" onClick={() => setSignOutConfirm(false)}>
                Cancel
              </button>
              <Button size="sm" onClick={() => void logout()}>
                Sign out anyway
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Docked here rather than inside a page: chat is meant to be visible
          almost everywhere, and re-mounting it per route would drop the socket
          and the backlog on every navigation. */}
      <ChatDock />

      {tourOpen && me && (
        <OverhaulTour userId={me.user.id} onClose={() => setTourOpen(false)} />
      )}

      <Toasts />

      {/* Badges take the screen before milestones do: a tier-up is the larger
          event, and being shown the small celebration first then interrupted by
          the big one reads backwards. */}
      {pendingBadges.length > 0 && (
        <BadgeFanfareQueue awards={pendingBadges} onDone={clearBadges} />
      )}

      {pendingBadges.length === 0 && pendingAchievements[0] && (
        <AchievementModal achievement={pendingAchievements[0]} onClose={clearAchievement} />
      )}
    </div>
  );
}

function MobileMenu({
  username,
  isAdmin,
  isGuest,
  onClose,
  onLogout,
}: {
  username: string;
  isAdmin: boolean;
  isGuest: boolean;
  onClose: () => void;
  onLogout: () => void;
}) {
  const shown = new Set(MOBILE_NAV.map((item) => item.to));
  const rest = ALL_NAV.filter((item) => !shown.has(item.to));

  return (
    <div
      className="modal-backdrop"
      onClick={onClose}
      style={{ alignItems: 'flex-end' }}
      role="dialog"
      aria-label="More pages"
    >
      <div
        className="pxframe pxframe--raised"
        onClick={(event) => event.stopPropagation()}
        style={{
          width: '100%',
          padding: 'var(--gap)',
          marginBottom: 'calc(84px + env(safe-area-inset-bottom))',
        }}
      >
        <div className="grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)', gap: 'var(--gap-sm)' }}>
          {rest.map((item) => (
            <Link key={item.to} to={item.to} className="btn btn--sm">
              {item.label}
            </Link>
          ))}
          <Link to={`/trainer/${username}`} className="btn btn--sm btn--info">
            My Profile
          </Link>
          <Link to="/settings" className="btn btn--sm">
            Settings
          </Link>
          {isGuest && (
            <Link to="/claim" className="btn btn--sm btn--gold">
              Claim account
            </Link>
          )}
          {isAdmin && (
            <Link to="/admin" className="btn btn--sm btn--special">
              Admin
            </Link>
          )}
          <a
            className="btn btn--sm btn--info"
            href={DISCORD_INVITE}
            target="_blank"
            rel="noreferrer noopener"
            onClick={onClose}
          >
            Discord ↗
          </a>
          <Button size="sm" onClick={onLogout}>
            Sign out
          </Button>
        </div>
      </div>
    </div>
  );
}

function Toasts() {
  const { toasts, dismiss } = useToast();
  return (
    <div className="toasts" aria-live="polite" aria-atomic="false">
      {toasts.map((toast) => (
        <div
          key={toast.id}
          className={`pxframe toast toast--${toast.kind}`}
          onClick={() => dismiss(toast.id)}
        >
          <span className="toast__icon" aria-hidden>
            {toast.icon}
          </span>
          <span>{toast.message}</span>
        </div>
      ))}
    </div>
  );
}

const BADGE_ART: Record<string, string> = {
  egg: '◓',
  dex: '▤',
  sparkle: '✦',
  heart: '♥',
  crown: '♛',
  star: '★',
  type: '◈',
  evolve: '⟳',
};

function AchievementModal({
  achievement,
  onClose,
}: {
  achievement: {
    name: string;
    description: string;
    badge: string;
    rewardCoins: number;
    rewardTokens: number;
    rewardTitle: string | null;
  };
  onClose: () => void;
}) {
  return (
    <Modal onClose={onClose} labelledBy="achievement-title">
      <div className="center stack" style={{ position: 'relative' }}>
        <Confetti />
        <div
          style={{
            fontSize: '3.5rem',
            lineHeight: 1,
            color: 'var(--gold)',
            textShadow: '3px 3px 0 var(--ink)',
            animation: 'hatch-pop 700ms var(--ease-pop) both',
          }}
          aria-hidden
        >
          {BADGE_ART[achievement.badge] ?? '★'}
        </div>
        <div className="mono tiny" style={{ letterSpacing: '0.2em', color: 'var(--gold-dark)' }}>
          ACHIEVEMENT UNLOCKED
        </div>
        <h2 id="achievement-title">{achievement.name}</h2>
        <p className="muted">{achievement.description}</p>

        {(achievement.rewardCoins > 0 || achievement.rewardTokens > 0 || achievement.rewardTitle) && (
          <div className="pxwell" style={{ padding: 'var(--gap-sm)', width: '100%' }}>
            <div className="row" style={{ justifyContent: 'center', gap: 'var(--gap)' }}>
              {achievement.rewardCoins > 0 && (
                <span className="row" style={{ gap: 5 }}>
                  <span className="coin__dot coin__dot--gold" aria-hidden />
                  <span className="mono">+{formatNumber(achievement.rewardCoins)}</span>
                </span>
              )}
              {achievement.rewardTokens > 0 && (
                <span className="row" style={{ gap: 5 }}>
                  <span className="coin__dot coin__dot--token" aria-hidden />
                  <span className="mono">+{formatNumber(achievement.rewardTokens)}</span>
                </span>
              )}
            </div>
            {achievement.rewardTitle && (
              <div className="tiny center" style={{ marginTop: 8 }}>
                New title unlocked: <strong>{achievement.rewardTitle}</strong>
              </div>
            )}
          </div>
        )}

        <Button variant="gold" size="lg" onClick={onClose}>
          Nice!
        </Button>
      </div>
    </Modal>
  );
}
